import { X } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { EnumSelect } from "@/components/shared/enum-select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import type { PhotoFilters as Filters } from "@/hooks/use-projects";
import { ALBUM_GROUP_OPTIONS, ALBUM_GROUPS } from "@/lib/enums";
import { companyLabel } from "@/lib/format";
import { NO_COMPANY, type ProjectDetail } from "@/schemas/photos";

const ORDER_OPTIONS = [
  { value: "desc", label: "Newest first" },
  { value: "asc", label: "Oldest first" },
] as const;

/** The album choices every filter bar offers, in the project's own order. */
export function albumFilterOptions(project: ProjectDetail) {
  return project.albums.map((album) => ({ value: album.id, label: album.name }));
}

export function hasActiveFilters(filters: Filters): boolean {
  const { album, group, from, to, company, make, model, serial } = filters;
  return Boolean(album || group || from || to || company || make || model || serial);
}

/** Text filter over nameplate readings. Waits for typing to stop before it asks the server. */
function ReadingFilter({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string | undefined;
  onChange: (value: string | undefined) => void;
}) {
  const [draft, setDraft] = useState(value ?? "");
  const debounced = useDebouncedValue(draft, 300);
  const sent = useRef(value ?? "");

  // "Clear filters" and back and forward change the URL under the box.
  useEffect(() => {
    if ((value ?? "") !== sent.current) {
      sent.current = value ?? "";
      setDraft(value ?? "");
    }
  }, [value]);

  useEffect(() => {
    if (debounced !== draft || debounced === sent.current) return;
    sent.current = debounced;
    onChange(debounced || undefined);
  }, [debounced, draft, onChange]);

  const id = `photos-${label.toLowerCase()}`;
  return (
    <div className="space-y-1">
      <Label htmlFor={id} className="text-xs text-muted-foreground">
        {label}
      </Label>
      <Input
        id={id}
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        className="w-full md:w-36"
      />
    </div>
  );
}

export function PhotoFilters({
  project,
  filters,
  onChange,
  onClear,
}: {
  project: ProjectDetail;
  filters: Filters;
  onChange: (next: Partial<Filters>) => void;
  onClear: () => void;
}) {
  const albumOptions = albumFilterOptions(project);
  const onMake = useCallback((make?: string) => onChange({ make }), [onChange]);
  const onModel = useCallback((model?: string) => onChange({ model }), [onChange]);
  const onSerial = useCallback((serial?: string) => onChange({ serial }), [onChange]);
  const companyOptions = [
    ...project.companies.map((company) => ({ value: company, label: companyLabel(company) })),
    ...(project.hasNoCompanyRows ? [{ value: NO_COMPANY, label: "No company recorded" }] : []),
  ];

  return (
    <div className="grid grid-cols-2 items-end gap-2 md:flex md:flex-wrap">
      <EnumSelect
        aria-label="Album"
        value={filters.album ?? ""}
        onValueChange={(album) => onChange({ album: album || undefined })}
        options={albumOptions}
        allOption={{ label: "All albums" }}
        className="col-span-2 w-full min-w-0 md:w-56"
      />
      <EnumSelect
        aria-label="Group"
        value={filters.group ?? ""}
        onValueChange={(group) => onChange({ group: ALBUM_GROUPS.find((g) => g === group) })}
        options={ALBUM_GROUP_OPTIONS}
        allOption={{ label: "All groups" }}
        className="w-full min-w-0 md:w-40"
      />
      <EnumSelect
        aria-label="Company"
        value={filters.company ?? ""}
        onValueChange={(company) => onChange({ company: company || undefined })}
        options={companyOptions}
        allOption={{ label: "All companies" }}
        className="w-full min-w-0 md:w-44"
      />
      <div className="space-y-1">
        <Label htmlFor="photos-from" className="text-xs text-muted-foreground">
          From
        </Label>
        <Input
          id="photos-from"
          type="date"
          value={filters.from ?? ""}
          onChange={(event) => onChange({ from: event.target.value || undefined })}
          className="w-full md:w-40"
        />
      </div>
      <div className="space-y-1">
        <Label htmlFor="photos-to" className="text-xs text-muted-foreground">
          To
        </Label>
        <Input
          id="photos-to"
          type="date"
          value={filters.to ?? ""}
          onChange={(event) => onChange({ to: event.target.value || undefined })}
          className="w-full md:w-40"
        />
      </div>
      <ReadingFilter label="Make" value={filters.make} onChange={onMake} />
      <ReadingFilter label="Model" value={filters.model} onChange={onModel} />
      <ReadingFilter label="Serial" value={filters.serial} onChange={onSerial} />
      <EnumSelect
        aria-label="Order"
        value={filters.order}
        onValueChange={(order) => onChange({ order: order === "asc" ? "asc" : "desc" })}
        options={ORDER_OPTIONS}
        className="w-full min-w-0 md:w-40"
      />
      {hasActiveFilters(filters) && (
        <Button variant="ghost" onClick={onClear} className="w-full md:w-auto">
          <X className="size-4" />
          Clear filters
        </Button>
      )}
    </div>
  );
}
